<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
// import { computed } from 'vue'; 
import Dialog from 'primevue/dialog';

interface Props {
    visible: boolean;
    canReplayTour?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:visible': [value: boolean];
    cancel: [];
    replayTour: [];
}>();

// const documentationUrl = computed(() => (process.env.VUE_APP_HELP_DOCUMENTATION_URL ?? 'https://creditorportalservice-753193062349.us-central1.run.app/docs/api#/').trim());
// const trainingVideoUrl = computed(() => (process.env.VUE_APP_HELP_TRAINING_VIDEO_URL ?? 'https://drive.google.com/file/d/1pSbKjYgZjh55ok6BFcshXzCaHsHZhp6M/preview').trim());
const supportEmail = 'creditors@setforth.com';
const supportEmailHref = `mailto:${supportEmail}`;

function close() {
    emit('update:visible', false);
    emit('cancel');
}
</script>

<template>
    <Dialog
        :visible="props.visible"
        modal
        :blockScroll="true"
        :dismissableMask="true"
        :closable="false"
        :draggable="false"
        :pt="{
            root: { class: 'help-support-modal' },
            header: { style: { display: 'none' } },
            content: { class: 'help-support-modal__content' },
            footer: { style: { display: 'none' } },
            mask: { class: 'help-support-modal__mask' },
        }"
        @update:visible="emit('update:visible', $event)"
    >
        <div class="help-support-modal__topbar">
            <button
                type="button"
                class="help-support-modal__close"
                aria-label="Close help and support modal"
                @click="close"
            >
                <i class="fa-solid fa-xmark" aria-hidden="true" />
            </button>
        </div>

        <section class="help-support-modal__section help-support-modal__section--intro">
            <h2 class="help-support-modal__heading">New to FORTH Creditor Portal?</h2>
            <p class="help-support-modal__copy">
                Check out our onboarding tour for new users.
            </p>

            <div class="help-support-modal__actions">
                <button
                    v-if="canReplayTour"
                    type="button"
                    class="help-support-modal__action"
                    @click="emit('replayTour')"
                >
                    Replay product tour
                </button>
                <!-- 
                    v-if="documentationUrl"
                    :href="documentationUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="help-support-modal__action"
                >
                    View Documentation &amp; Job Aids
                </a>
                <button
                    v-else
                    type="button"
                    class="help-support-modal__action help-support-modal__action--disabled"
                    disabled
                >
                    View Documentation &amp; Job Aids
                </button>

                <a
                    v-if="trainingVideoUrl"
                    :href="trainingVideoUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="help-support-modal__action"
                >
                    View Training Video
                </a>
                <button
                    v-else
                    type="button"
                    class="help-support-modal__action help-support-modal__action--disabled"
                    disabled
                >
                    View Training Video
                </button>
                -->
            </div>
        </section>

        <span class="help-support-modal__divider" aria-hidden="true" />

        <section class="help-support-modal__section help-support-modal__section--support">
            <h2 class="help-support-modal__heading">Contact Our Support</h2>
            <p class="help-support-modal__support-copy">
                If you have any questions, comments or concerns please contact
                <span class="help-support-modal__support-line">
                    FORTH Creditor Support Team at
                    <a :href="supportEmailHref" class="help-support-modal__support-link">{{ supportEmail }}</a>
                </span>
            </p>
        </section>
    </Dialog>
</template>

<style lang="scss" scoped>
.help-support-modal__topbar {
    display: flex;
    justify-content: flex-end;
}

.help-support-modal__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    color: $main-orange;
    cursor: pointer;

    i {
        font-size: 20px;
        line-height: 20px;
        font-weight: 900;
    }
}

.help-support-modal__section {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.help-support-modal__section--intro {
    padding-top: 8px;
}

.help-support-modal__section--support {
    padding-top: 34px;
}

.help-support-modal__heading {
    margin: 0;
    font-family: $font-family-archivo;
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
    color: $black-color;
}

.help-support-modal__copy {
    margin: 18px 0 0;
    font-family: $font-family-archivo;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    text-align: center;
    color: $black-color;
}

.help-support-modal__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    margin-top: 34px;
}

.help-support-modal__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 46px;
    min-height: 46px;
    padding: 0 20px;
    border: 1px solid $border-gray-color;
    border-radius: 10px;
    background: $white-color;
    font-family: $font-family-archivo;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 17px;
    color: rgba($black-color, 0.7);
    text-align: center;
    text-decoration: none;
    transition: border-color 0.15s ease, color 0.15s ease;
    box-sizing: border-box;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: $main-orange;
        color: $main-orange;
    }

    &:focus-visible {
        outline: 2px solid $main-orange;
        outline-offset: 2px;
    }
}

.help-support-modal__action--disabled {
    cursor: default;
}

.help-support-modal__divider {
    display: block;
    width: 100%;
    height: 2px;
    margin: 46px 0 0;
    background: $hr-gray-color;
}

.help-support-modal__support-copy {
    max-width: 760px;
    margin: 26px 0 0;
    font-family: $font-family-archivo;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    text-align: center;
    color: $black-color;
}

.help-support-modal__support-line {
    display: block;
}

.help-support-modal__support-link {
    color: $black-color;
    text-decoration-thickness: 1px;
    text-decoration: underline;
    text-underline-offset: 5px;
}

@media (max-width: 900px) {
    .help-support-modal__section--intro {
        padding-top: 12px;
    }

    .help-support-modal__actions {
        flex-direction: column;
        width: 100%;
        margin-top: 32px;
    }

    .help-support-modal__action {
        width: 100%;
        min-width: 0;
    }

    .help-support-modal__divider {
        margin-top: 48px;
    }
}

@media (max-width: 640px) {
    .help-support-modal__heading {
        font-size: 20px;
        line-height: 28px;
    }

    .help-support-modal__copy,
    .help-support-modal__support-copy {
        font-size: 15px;
        line-height: 24px;
    }
}
</style>

<style lang="scss">
.p-dialog.help-support-modal {
    --p-dialog-border-radius: 5px;
    --p-dialog-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
    width: min(930px, calc(100vw - 32px));
    height: 458px;
    max-height: calc(100vh - 32px);
    border: 1px solid #EBEEF6;
    background: #FFFFFF;
}

.p-dialog.help-support-modal .help-support-modal__content {
    box-sizing: border-box;
    height: 100%;
    padding: 18px 30px 26px !important;
    overflow: auto !important;
}

@media (max-width: 640px) {
    .p-dialog.help-support-modal {
        width: calc(100vw - 20px);
        height: auto;
    }

    .p-dialog.help-support-modal .help-support-modal__content {
        padding: 20px 18px 28px !important;
    }
}
</style>
